import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Loader2, Sparkles, Image as ImageIcon, ExternalLink, RefreshCw, Filter, AlertTriangle, CheckCircle2, ImageOff, FileText, ListChecks } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// All issue keys returned by the backend + their UI metadata. Keeps the filter
// chip row and the per-row badges in one place.
const ISSUE_META = {
  missing_description:    { label: 'No description',  short: 'No desc',     color: 'bg-red-100 text-red-800 border-red-200',         icon: FileText },
  missing_image:          { label: 'No image',        short: 'No image',    color: 'bg-orange-100 text-orange-800 border-orange-200',icon: ImageOff },
  single_image_only:      { label: 'Only 1 image',    short: '1 image',     color: 'bg-amber-100 text-amber-800 border-amber-200',   icon: ImageIcon },
  missing_alt_text:       { label: 'No image alt',    short: 'No alt',      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',icon: ImageIcon },
  missing_barcode:        { label: 'No barcode',      short: 'No barcode',  color: 'bg-slate-100 text-slate-700 border-slate-200',   icon: ListChecks },
  missing_or_weak_title:  { label: 'Weak title',      short: 'Weak title',  color: 'bg-purple-100 text-purple-800 border-purple-200',icon: AlertTriangle },
  no_seo:                 { label: 'No SEO',          short: 'No SEO',      color: 'bg-indigo-100 text-indigo-800 border-indigo-200',icon: ListChecks },
};

export default function ShopifyHealth() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);          // { counts, products, shop_admin_base }
  const [filter, setFilter] = useState('needs_attention'); // 'needs_attention' | issue key | 'all'
  const [search, setSearch] = useState('');

  // Description-generator dialog
  const [genOpen, setGenOpen] = useState(false);
  const [genProduct, setGenProduct] = useState(null);
  const [genTone] = useState('clinical');         // fixed for this brand — user can change later
  const [genWordCount, setGenWordCount] = useState(120);
  const [genExtraContext, setGenExtraContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [pushingDesc, setPushingDesc] = useState(false);

  // Bulk generate dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPin, setBulkPin] = useState('');
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const bulkPollRef = useRef(null);

  // Image upload dialog
  const [imgOpen, setImgOpen] = useState(false);
  const [imgProduct, setImgProduct] = useState(null);
  const [imgAlt, setImgAlt] = useState('');
  const [imgFile, setImgFile] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgFileRef = useRef(null);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/shopify/health-audit?limit=2000`, { headers: getAuthHeader(), timeout: 90000 });
      setData(res.data);
    } catch (e) {
      toast.error('Shopify audit failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); /* eslint-disable-next-line */ }, []);

  // Filter + search on the products returned by the audit
  const visible = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.products
      .filter(p => {
        if (filter === 'all') return true;
        if (filter === 'needs_attention') return p.needs_attention;
        return p.issues.includes(filter);
      })
      .filter(p => !q || (p.title || '').toLowerCase().includes(q) || (p.vendor || '').toLowerCase().includes(q));
  }, [data, filter, search]);

  // ───── Description generator ───────────────────────────────────────────────
  const openGenerator = (product) => {
    setGenProduct(product);
    setGeneratedHtml('');
    setGenExtraContext('');
    setGenWordCount(120);
    setGenOpen(true);
  };

  const runGenerator = async () => {
    if (!genProduct) return;
    setGenerating(true);
    setGeneratedHtml('');
    try {
      const res = await axios.post(`${API_URL}/shopify/health-audit/generate-description`, {
        product_id: genProduct.id,
        tone: genTone,
        word_count: genWordCount,
        extra_context: genExtraContext || null,
      }, { headers: getAuthHeader(), timeout: 60000 });
      setGeneratedHtml(res.data.generated_html || '');
      toast.success('Description generated. Review below, then push to Shopify.');
    } catch (e) {
      toast.error('Generate failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const pushDescription = async () => {
    if (!genProduct || !generatedHtml.trim()) return;
    setPushingDesc(true);
    try {
      await axios.post(`${API_URL}/shopify/health-audit/push-description`, {
        product_id: genProduct.id,
        body_html: generatedHtml,
      }, { headers: getAuthHeader(), timeout: 30000 });
      toast.success(`✓ "${genProduct.title}" updated on Shopify.`);
      setGenOpen(false);
      // Optimistically clear the missing_description issue locally — saves a 30s audit refetch
      setData(prev => prev ? {
        ...prev,
        products: prev.products.map(p => p.id === genProduct.id
          ? { ...p, issues: p.issues.filter(i => i !== 'missing_description' && i !== 'no_seo'), needs_attention: p.issues.filter(i => i !== 'missing_description' && i !== 'no_seo').length > 0, body_text_length: 1 }
          : p),
        counts: { ...prev.counts, missing_description: Math.max(0, (prev.counts.missing_description || 0) - 1) }
      } : prev);
    } catch (e) {
      toast.error('Push failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPushingDesc(false);
    }
  };

  // ───── Bulk generate ────────────────────────────────────────────────────────
  // Targets every product currently flagged as "missing_description" — same
  // set the dashboard shows under the "No description" filter. Limited to 1000
  // per run by the backend; the UI confirms the count + cost in the modal.
  const missingDescIds = useMemo(() => {
    if (!data) return [];
    return data.products.filter(p => p.issues.includes('missing_description')).map(p => p.id);
  }, [data]);

  const startBulkJob = async () => {
    if (!bulkPin.trim()) {
      toast.error('Enter the manager PIN.');
      return;
    }
    if (missingDescIds.length === 0) {
      toast.info('Nothing to bulk-generate — no products missing descriptions.');
      return;
    }
    setBulkStarting(true);
    try {
      const batch = missingDescIds.slice(0, 1000); // backend caps at 1000
      const res = await axios.post(`${API_URL}/shopify/health-audit/bulk-generate`, {
        product_ids: batch,
        manager_pin: bulkPin.trim(),
        tone: 'clinical',
        word_count: 120,
        auto_push: true,
      }, { headers: getAuthHeader(), timeout: 20000 });
      setBulkJobId(res.data.job_id);
      setBulkJob({ ...res.data, processed: 0, pushed: 0, failed: 0, total: res.data.total });
      setBulkPin('');
      toast.success(`Started bulk job for ${res.data.total} products (~$${res.data.estimated_cost_usd}).`);
    } catch (e) {
      toast.error('Could not start: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBulkStarting(false);
    }
  };

  // Poll the job every 2.5s while it's running; clear when done/failed.
  useEffect(() => {
    if (!bulkJobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/shopify/health-audit/bulk-job/${bulkJobId}`, { headers: getAuthHeader(), timeout: 15000 });
        if (cancelled) return;
        setBulkJob(res.data);
        if (res.data.status === 'done' || res.data.status === 'failed') {
          toast.success(`Bulk job ${res.data.status}: ${res.data.pushed} pushed, ${res.data.failed} failed.`);
          // Refresh the audit so counts drop
          fetchAudit();
        } else {
          bulkPollRef.current = setTimeout(poll, 2500);
        }
      } catch (e) {
        if (!cancelled) bulkPollRef.current = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (bulkPollRef.current) clearTimeout(bulkPollRef.current);
    };
    // eslint-disable-next-line
  }, [bulkJobId]);


  const openImageUploader = (product) => {
    setImgProduct(product);
    setImgAlt(product.title || '');
    setImgFile(null);
    setImgOpen(true);
  };

  const uploadImage = async () => {
    if (!imgProduct || !imgFile) {
      toast.error('Pick an image file first.');
      return;
    }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('product_id', imgProduct.id);
      fd.append('alt', imgAlt);
      fd.append('file', imgFile);
      const res = await axios.post(`${API_URL}/shopify/health-audit/push-image`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`✓ Image uploaded to "${imgProduct.title}".`);
      setImgOpen(false);
      // Optimistic local update
      setData(prev => prev ? {
        ...prev,
        products: prev.products.map(p => p.id === imgProduct.id
          ? { ...p,
              issues: p.issues.filter(i => i !== 'missing_image' && i !== 'single_image_only'),
              image_count: (p.image_count || 0) + 1,
              primary_image_url: p.primary_image_url || res.data.image?.src,
              needs_attention: p.issues.filter(i => i !== 'missing_image' && i !== 'single_image_only').length > 0,
            }
          : p),
        counts: { ...prev.counts, missing_image: Math.max(0, (prev.counts.missing_image || 0) - 1) }
      } : prev);
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingImg(false);
    }
  };

  // ───── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] flex-col gap-3 text-slate-500" data-testid="shopify-health-loading">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p>Scanning your Shopify catalog… (1–2 min for 2000+ products)</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-slate-500">
        <p>Could not load Shopify audit. Check your Shopify integration in Settings.</p>
        <Button onClick={fetchAudit} className="mt-3" data-testid="retry-shopify-audit">Retry</Button>
      </div>
    );
  }

  const c = data.counts;
  const adminBase = data.shop_admin_base || '';

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="shopify-health-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900" data-testid="shopify-health-h1">
            Shopify Health — Storefront Tidy-up
          </h1>
          <p className="text-slate-500 mt-1">
            One screen to spot every Shopify product missing a description, image or SEO basics — and fix them in bulk.
          </p>
        </div>
        <Button variant="outline" onClick={fetchAudit} disabled={loading} data-testid="refresh-shopify-audit">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Re-scan
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <SummaryCard label="Products in catalog" value={c.total_products} tone="slate" testid="card-total" />
        <SummaryCard label="Need attention" value={c.needs_attention} tone="red" testid="card-attention" />
        <SummaryCard label="No description" value={c.missing_description} tone="red" onClick={() => setFilter('missing_description')} active={filter === 'missing_description'} testid="card-no-desc" />
        <SummaryCard label="No image" value={c.missing_image} tone="orange" onClick={() => setFilter('missing_image')} active={filter === 'missing_image'} testid="card-no-image" />
        <SummaryCard label="Only 1 image" value={c.single_image_only} tone="amber" onClick={() => setFilter('single_image_only')} active={filter === 'single_image_only'} testid="card-one-image" />
        <SummaryCard label="No alt text" value={c.missing_alt_text} tone="yellow" onClick={() => setFilter('missing_alt_text')} active={filter === 'missing_alt_text'} testid="card-no-alt" />
      </div>

      {/* Bulk generate banner — only shows if there's something to bulk-generate */}
      {(c.missing_description > 0 || bulkJob) && (
        <Card className="border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-white" data-testid="bulk-banner">
          <CardContent className="p-4">
            {!bulkJob ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-bold text-emerald-900 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" />
                    Tidy the whole catalog in one click
                  </p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    AI-write descriptions for all <span className="font-semibold">{c.missing_description.toLocaleString('en-NZ')}</span> products that are missing one — using your supplier data so nothing is invented.
                    Est. cost: <span className="font-semibold">${(c.missing_description * 0.005).toFixed(2)} NZD</span> ({Math.min(c.missing_description, 1000)} per batch · ~0.5s each · gpt-4o)
                  </p>
                </div>
                <Button
                  onClick={() => setBulkOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid="open-bulk-generate"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Generate {Math.min(c.missing_description, 1000)} descriptions
                </Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="bulk-progress">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-emerald-900 flex items-center gap-1.5">
                    <Loader2 className={`w-4 h-4 ${bulkJob.status === 'running' ? 'animate-spin' : ''}`} />
                    Bulk job · {bulkJob.status}
                  </p>
                  <p className="text-sm font-mono">
                    {bulkJob.processed} / {bulkJob.total} processed · {bulkJob.pushed} pushed · {bulkJob.failed} failed
                  </p>
                </div>
                <div className="w-full h-2 bg-emerald-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 transition-all"
                    style={{ width: `${Math.min(100, (bulkJob.processed / Math.max(1, bulkJob.total)) * 100)}%` }}
                  />
                </div>
                {bulkJob.last_errors?.length > 0 && (
                  <details className="text-xs text-red-700">
                    <summary className="cursor-pointer">Recent errors ({bulkJob.last_errors.length})</summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {bulkJob.last_errors.slice(-5).map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </details>
                )}
                {bulkJob.status === 'done' && (
                  <Button size="sm" variant="ghost" onClick={() => { setBulkJob(null); setBulkJobId(null); }}>Dismiss</Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter chips + search */}
      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-slate-500"><Filter className="w-4 h-4" />Filter:</div>
          {[
            { id: 'needs_attention', label: 'All issues' },
            { id: 'missing_description', label: 'Missing description' },
            { id: 'missing_image', label: 'Missing image' },
            { id: 'missing_alt_text', label: 'Missing alt text' },
            { id: 'single_image_only', label: 'Only 1 image' },
            { id: 'all', label: 'All products' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === f.id ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >{f.label}</button>
          ))}
          <Input
            placeholder="Search title or vendor…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ml-auto w-60"
            data-testid="shopify-health-search"
          />
        </CardContent>
      </Card>

      <p className="text-sm text-slate-500" data-testid="result-count">
        Showing <span className="font-semibold text-slate-700">{visible.length}</span> of {data.products.length} {filter === 'all' ? 'products' : 'flagged products'}
      </p>

      {/* Products list */}
      <div className="space-y-2">
        {visible.length === 0 && (
          <Card><CardContent className="p-8 text-center text-slate-500">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <p>Nothing to fix here — your Shopify catalog passes this filter. 🎉</p>
          </CardContent></Card>
        )}
        {visible.map(p => (
          <ProductRow
            key={p.id}
            product={p}
            adminBase={adminBase}
            onGenerateDesc={() => openGenerator(p)}
            onUploadImage={() => openImageUploader(p)}
          />
        ))}
      </div>

      {/* Description generator dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b">
            <DialogTitle className="flex items-center gap-2 font-heading text-xl uppercase">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              AI Description — {genProduct?.title}
            </DialogTitle>
            <DialogDescription>
              Clinical, evidence-based tone — no hype, no medical claims. Always reviewed before pushing to Shopify.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Target word count</Label>
                <Input
                  type="number"
                  min="40"
                  max="250"
                  value={genWordCount}
                  onChange={e => setGenWordCount(parseInt(e.target.value) || 120)}
                  className="h-9"
                  data-testid="gen-word-count"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Tone</Label>
                <Input value="Clinical (evidence-based)" disabled className="h-9 bg-slate-50" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Extra context (optional)</Label>
              <textarea
                value={genExtraContext}
                onChange={e => setGenExtraContext(e.target.value)}
                placeholder="e.g. 'Active ingredient is 500mg curcumin extract', 'For adults only', 'Best taken before bed'…"
                className="w-full text-sm rounded border border-slate-300 p-2 min-h-[60px] focus:ring-2 focus:ring-emerald-500"
                data-testid="gen-extra-context"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Optional — anything the supplier blurb mentioned that you want included verbatim.
              </p>
            </div>
            <Button
              onClick={runGenerator}
              disabled={generating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="generate-desc-btn"
            >
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                : <><Sparkles className="w-4 h-4 mr-2" />{generatedHtml ? 'Regenerate' : 'Generate description'}</>}
            </Button>
            {generatedHtml && (
              <div className="space-y-2">
                <Label className="text-[11px] uppercase text-slate-500">Generated description (editable HTML)</Label>
                <textarea
                  value={generatedHtml}
                  onChange={e => setGeneratedHtml(e.target.value)}
                  className="w-full text-sm font-mono rounded border border-slate-300 p-2 min-h-[180px] focus:ring-2 focus:ring-emerald-500"
                  data-testid="generated-desc-html"
                />
                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="generated-desc-preview">
                  <p className="text-[10px] uppercase text-slate-400 mb-1">Preview</p>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: generatedHtml }} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t bg-white px-6 py-3 shrink-0">
            <Button variant="outline" onClick={() => setGenOpen(false)}>Close</Button>
            <Button
              onClick={pushDescription}
              disabled={!generatedHtml.trim() || pushingDesc}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="push-desc-btn"
            >
              {pushingDesc
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Pushing…</>
                : <>✓ Push to Shopify</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image upload dialog */}
      <Dialog open={imgOpen} onOpenChange={setImgOpen}>        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading uppercase">
              <ImageIcon className="w-5 h-5 text-emerald-600" />
              Upload image
            </DialogTitle>
            <DialogDescription className="text-xs">
              {imgProduct?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Image file</Label>
              <input
                ref={imgFileRef}
                type="file"
                accept="image/*"
                onChange={e => setImgFile(e.target.files?.[0] || null)}
                className="w-full text-sm border border-slate-300 rounded p-2"
                data-testid="img-file-input"
              />
              {imgFile && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Selected: {imgFile.name} ({(imgFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Alt text (SEO + accessibility)</Label>
              <Input
                value={imgAlt}
                onChange={e => setImgAlt(e.target.value)}
                placeholder="Describe the image for screen readers / Google"
                className="h-9"
                data-testid="img-alt-input"
              />
            </div>
            {imgFile && (
              <div className="rounded border bg-slate-50 p-2 max-h-48 overflow-hidden">
                <img src={URL.createObjectURL(imgFile)} alt="preview" className="w-full h-auto object-contain max-h-44" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImgOpen(false)}>Cancel</Button>
            <Button
              onClick={uploadImage}
              disabled={!imgFile || uploadingImg}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="upload-img-btn"
            >
              {uploadingImg
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</>
                : <>Upload to Shopify</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate dialog — manager PIN required */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading uppercase">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Bulk-generate descriptions
            </DialogTitle>
            <DialogDescription>
              Manager-only action. Uses your supplier data so nothing is invented.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border rounded-lg p-3 space-y-1.5">
              <p className="flex justify-between"><span className="text-slate-500">Products targeted</span><span className="font-bold">{Math.min(missingDescIds.length, 1000).toLocaleString('en-NZ')}</span></p>
              <p className="flex justify-between"><span className="text-slate-500">Model</span><span className="font-mono">gpt-4o</span></p>
              <p className="flex justify-between"><span className="text-slate-500">Cost per description</span><span className="font-mono">~$0.005 NZD</span></p>
              <p className="flex justify-between text-emerald-800"><span className="font-semibold">Estimated total</span><span className="font-bold text-base">~${(Math.min(missingDescIds.length, 1000) * 0.005).toFixed(2)} NZD</span></p>
              <p className="flex justify-between"><span className="text-slate-500">Auto-push to Shopify</span><span className="font-bold text-emerald-700">Yes</span></p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1">⚠ This will edit your live Shopify storefront.</p>
              <ul className="space-y-0.5 list-disc list-inside">
                <li>Descriptions are written from your supplier data + existing Shopify info only.</li>
                <li>No therapeutic claims, no fabricated ingredients, no marketing hype.</li>
                <li>Runs at ~0.5s per product — 449 will take ~4 minutes total.</li>
                <li>Safe to close this page — the job continues in the background.</li>
              </ul>
            </div>
            <div>
              <Label htmlFor="bulk-pin" className="text-[11px] uppercase text-slate-500">Manager PIN to confirm</Label>
              <Input
                id="bulk-pin"
                type="password"
                value={bulkPin}
                onChange={(e) => setBulkPin(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') startBulkJob(); }}
                placeholder="••••"
                className="h-10 font-mono text-lg"
                data-testid="bulk-pin-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkStarting}>Cancel</Button>
            <Button
              onClick={async () => { await startBulkJob(); setBulkOpen(false); }}
              disabled={bulkStarting || !bulkPin.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-bulk-generate"
            >
              {bulkStarting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>
                : <>Start bulk job</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Small components kept in-file to avoid one-off util files ────────────────
function SummaryCard({ label, value, tone, onClick, active, testid }) {
  const toneClass = {
    slate:  'bg-slate-50 border-slate-200 text-slate-800',
    red:    'bg-red-50 border-red-200 text-red-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    amber:  'bg-amber-50 border-amber-200 text-amber-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  }[tone] || 'bg-slate-50';
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-lg border-2 p-3 transition ${toneClass} ${onClick ? 'hover:shadow-md cursor-pointer' : ''} ${active ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{(value ?? 0).toLocaleString('en-NZ')}</p>
    </button>
  );
}

function ProductRow({ product, adminBase, onGenerateDesc, onUploadImage }) {
  const hasNoImage   = product.issues.includes('missing_image');
  const hasNoDesc    = product.issues.includes('missing_description');
  return (
    <Card data-testid={`product-row-${product.id}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <div className="w-16 h-16 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {product.primary_image_url
            ? <img src={product.primary_image_url} alt={product.primary_image_alt || product.title} className="w-full h-full object-cover" />
            : <ImageOff className="w-6 h-6 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate" title={product.title}>{product.title}</p>
          <p className="text-xs text-slate-500">
            {product.vendor || '—'}
            {product.price && ` · $${product.price}`}
            {product.image_count !== undefined && ` · ${product.image_count} image${product.image_count === 1 ? '' : 's'}`}
            {product.body_text_length > 0 && ` · ${product.body_text_length}-char description`}
          </p>
          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {product.issues.map(iss => {
              const meta = ISSUE_META[iss];
              if (!meta) return null;
              return (
                <Badge key={iss} className={`text-[10px] border ${meta.color}`} data-testid={`issue-${product.id}-${iss}`}>
                  {meta.short}
                </Badge>
              );
            })}
            {product.issues.length === 0 && (
              <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">All good</Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={onGenerateDesc}
            variant={hasNoDesc ? 'default' : 'outline'}
            className={hasNoDesc ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            data-testid={`gen-desc-${product.id}`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {hasNoDesc ? 'Write desc' : 'Rewrite'}
          </Button>
          <Button
            size="sm"
            onClick={onUploadImage}
            variant={hasNoImage ? 'default' : 'outline'}
            className={hasNoImage ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
            data-testid={`upload-img-${product.id}`}
          >
            <ImageIcon className="w-3.5 h-3.5 mr-1" />
            {hasNoImage ? 'Add image' : 'Add another'}
          </Button>
          {adminBase && (
            <a
              href={`${adminBase}${product.admin_url}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-slate-500 hover:text-emerald-700 inline-flex items-center gap-0.5"
              data-testid={`open-shopify-${product.id}`}
            >
              Shopify admin <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
